import { Injectable, computed, inject, signal } from '@angular/core';
import { track } from './analytics';
import { ListingsService } from './listings.service';

export interface FilterOption {
  name: string;
  slug: string;
}

interface FilterOptionsPayload {
  regions: FilterOption[];
  categories: FilterOption[];
}

// Audience toggle keys - must match Listings::Repository::AUDIENCE_COLUMNS.
export const AUDIENCE_KEYS = ['kid', 'baby', 'pet', 'wheelchair'] as const;
export type AudienceKey = (typeof AUDIENCE_KEYS)[number];

@Injectable({ providedIn: 'root' })
export class MapFiltersService {
  private readonly listingsService = inject(ListingsService);

  // Options for the drawer dropdowns, embedded in the planner view.
  readonly regions: FilterOption[];
  readonly categories: FilterOption[];

  readonly open = signal(false);
  readonly regionSlugs = signal<Set<string>>(new Set());
  readonly categorySlugs = signal<Set<string>>(new Set());
  readonly audience = signal<Set<AudienceKey>>(new Set());

  readonly activeCount = computed(
    () =>
      this.regionSlugs().size +
      this.categorySlugs().size +
      this.audience().size
  );

  constructor() {
    const payload = this.readOptions();
    this.regions = payload.regions;
    this.categories = payload.categories;
  }

  openDrawer(): void {
    this.open.set(true);
  }

  closeDrawer(): void {
    this.open.set(false);
  }

  toggleRegion(slug: string): void {
    this.regionSlugs.set(this.toggled(this.regionSlugs(), slug));
  }

  toggleCategory(slug: string): void {
    this.categorySlugs.set(this.toggled(this.categorySlugs(), slug));
  }

  toggleAudience(key: AudienceKey): void {
    this.audience.set(this.toggled(this.audience(), key));
  }

  reset(): void {
    this.regionSlugs.set(new Set());
    this.categorySlugs.set(new Set());
    this.audience.set(new Set());
    void this.listingsService.fetchMapListings();
  }

  apply(): void {
    track('planner_filter_apply', {
      regions: [...this.regionSlugs()].join(','),
      categories: [...this.categorySlugs()].join(','),
      audiences: [...this.audience()].join(','),
    });
    void this.listingsService.fetchMapListings(this.toQueryString());
    this.closeDrawer();
  }

  private toQueryString(): string {
    // Keys use the `[]` suffix so Rack parses repeated values into an array
    // (`region=a&region=b` keeps only the last value; `region[]=a&region[]=b`
    // builds ["a", "b"]). Array(params[:region]) in Listings::Filters reads it.
    const params = new URLSearchParams();
    for (const slug of this.regionSlugs()) params.append('region[]', slug);
    for (const slug of this.categorySlugs()) params.append('category[]', slug);
    for (const key of this.audience()) params.append('audience[]', key);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }

  // Returns a new Set with `value` toggled - keeps signal updates immutable.
  private toggled<T>(set: Set<T>, value: T): Set<T> {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  }

  private readOptions(): FilterOptionsPayload {
    const el = document.getElementById('djir-filter-options');
    if (!el?.textContent) return { regions: [], categories: [] };
    try {
      const parsed = JSON.parse(el.textContent) as Partial<FilterOptionsPayload>;
      return {
        regions: parsed.regions ?? [],
        categories: parsed.categories ?? [],
      };
    } catch (e) {
      console.error('[map-filters] failed to parse filter options', e);
      return { regions: [], categories: [] };
    }
  }
}
